import React from 'react';
import ServiceTemplate from '@/components/ServiceTemplate';

const complianceData = {
  llp: {
    title: 'LLP Compliance Services',
    description: 'Complete annual compliance for Limited Liability Partnerships. Stay compliant with MCA regulations and avoid penalties with expert CA guidance.',
    keywords: 'LLP compliance, Form 8, Form 11, LLP annual return, LLP audit, MCA compliance',
    
    detailedDescription: (
      <div className="space-y-4">
        <p>
          Every Limited Liability Partnership (LLP) must comply with annual filing requirements under the LLP Act 2008. 
          Non-compliance can lead to heavy penalties, late fees, and even LLP strike-off by the Registrar of Companies. 
          Our comprehensive LLP compliance package ensures your business remains in good standing with regulatory authorities.
        </p>
        <p>
          <strong>Why is LLP Compliance Important?</strong> The Ministry of Corporate Affairs (MCA) mandates annual filings 
          for all LLPs, regardless of turnover or profitability. Penalties for late filing start at ₹100 per day with no 
          maximum cap, meaning delays of even a few months can result in penalties of ₹10,000 or more. 
          Additionally, banks and business partners often require compliance certificates.
        </p>
      </div>
    ),
    
    benefits: [
      'Avoid heavy penalties (₹100/day for late filing)',
      'Maintain good standing with MCA',
      'Form 8 & Form 11 filing with expert review',
      'Statement of Account & Solvency preparation',
      'Income Tax Return filing by CA',
      'Tax planning and optimization advice',
      'Year-round compliance consultation',
      'Timely reminders for all deadlines',
    ],
    
    documents: [
      'Previous year financial statements',
      'Bank statements for the year',
      'Sales and purchase invoices',
      'Expense bills and vouchers',
      'Partner capital contribution details',
      'LLP Agreement (for reference)',
      'Designated Partner DSC',
      'Previous filing acknowledgments',
    ],
    
    process: [
      'Collection of financial data and documents',
      'Preparation of Statement of Account & Solvency',
      'Filing of Form 11 (Annual Return) by 30th May',
      'Filing of Form 8 (Statement of Account) by 30th October',
      'Income Tax Return filing by 31st July',
      'Tax audit coordination (if applicable)',
    ],
    
    timeline: 'Annual (May-October)',
    pricing: '₹12,999/year',
    
    additionalInfo: (
      <div className="space-y-3">
        <p><strong>Key Deadlines:</strong></p>
        <ul className="list-disc list-inside ml-4 space-y-1">
          <li>Form 11 (Annual Return): 30th May</li>
          <li>Form 8 (Statement of Accounts): 30th October</li>
          <li>Income Tax Return: 31st July (or 30th Sept if audit required)</li>
        </ul>
        <p className="mt-3"><strong>Audit Requirement:</strong></p>
        <ul className="list-disc list-inside ml-4 space-y-1">
          <li>Audit mandatory if turnover &gt; ₹40 lakhs OR contribution &gt; ₹25 lakhs</li>
          <li>Tax audit if turnover exceeds ₹1 crore</li>
        </ul>
      </div>
    ),
    
    faqs: [
      {
        question: 'What are the due dates for LLP compliance?',
        answer: 'Form 11 (Annual Return) is due by 30th May and Form 8 (Statement of Account & Solvency) is due by 30th October every year. Income Tax Return is due by 31st July (or 30th September if audit is applicable).',
      },
      {
        question: 'What happens if LLP compliance is not done?',
        answer: 'Late filing attracts penalty of ₹100 per day with no cap. Continued non-compliance can lead to LLP being struck off by ROC, designated partners being disqualified, and inability to close bank accounts or sign contracts.',
      },
      {
        question: 'When is audit mandatory for LLP?',
        answer: 'Statutory audit is mandatory if LLP turnover exceeds ₹40 lakhs OR contribution from partners exceeds ₹25 lakhs in any financial year. Tax audit is required if turnover exceeds ₹1 crore.',
      },
    ],
  },
  
  opc: {
    title: 'OPC Compliance Services',
    description: 'Annual compliance services for One Person Company. Simplified compliance with expert CA support for board meetings, ROC filings, and tax returns.',
    keywords: 'OPC compliance, one person company compliance, MGT-7A, AOC-4, OPC annual return',
    
    detailedDescription: (
      <div className="space-y-4">
        <p>
          One Person Companies (OPCs) enjoy relaxed compliance requirements compared to regular Private Limited Companies, 
          but still must maintain statutory compliance with MCA and Income Tax regulations. Our OPC compliance package 
          ensures you meet all requirements without the hassle of managing multiple deadlines.
        </p>
        <p>
          <strong>Simplified Compliance for OPC:</strong> OPCs file simplified annual return (MGT-7A instead of MGT-7), 
          don't require AGM, and need only 2 board meetings per year. However, financial statements (AOC-4) filing, 
          statutory audit, and income tax compliance are similar to Private Limited Companies.
        </p>
      </div>
    ),
    
    benefits: [
      'No AGM requirement – saves time and documentation',
      'Only 2 board meetings required per year',
      'Simplified MGT-7A annual return filing',
      'Financial statements preparation (AOC-4)',
      'Statutory audit coordination',
      'Income Tax Return (ITR-6) filing',
      'Board meeting minutes drafting',
      'Year-round compliance support',
    ],
    
    documents: [
      'Previous year financial statements',
      'Bank statements for the year',
      'All business invoices and bills',
      'Salary records and TDS details',
      'Director DSC',
      'Previous ROC filings',
      'Tax payment challans',
      'Investment and capital records',
    ],
    
    process: [
      'Financial statements preparation',
      'Coordination with statutory auditor',
      'Board meeting for accounts approval',
      'Filing of Form AOC-4 within 180 days of FY end',
      'Filing of Form MGT-7A within 60 days of FY end',
      'Income Tax Return filing by 31st October',
    ],
    
    timeline: 'Annual',
    pricing: '₹12,999/year',
    
    additionalInfo: (
      <div className="space-y-3">
        <p><strong>OPC Specific Relaxations:</strong></p>
        <ul className="list-disc list-inside ml-4 space-y-1">
          <li>No requirement to hold AGM</li>
          <li>Only 2 board meetings (half-year gap) required</li>
          <li>Financial statements can be signed by 1 director only</li>
          <li>Simplified annual return MGT-7A instead of MGT-7</li>
        </ul>
      </div>
    ),
    
    faqs: [
      {
        question: 'How many board meetings are required for OPC?',
        answer: 'OPC requires minimum 2 board meetings per year with a gap of at least 90 days between them. This is relaxed compared to 4 meetings required for regular Private Limited Companies.',
      },
      {
        question: 'Is AGM required for OPC?',
        answer: 'No, OPC is not required to hold Annual General Meeting. The sole member can pass resolutions by written communication to the company, which must be recorded in the minutes book.',
      },
      {
        question: 'What are the annual filings for OPC?',
        answer: 'OPC must file Form AOC-4 (financial statements) within 180 days of financial year end and Form MGT-7A (simplified annual return) within 60 days of financial year end. Income Tax Return by 31st October.',
      },
    ],
  },
  
  proprietorship: {
    title: 'Proprietorship Compliance',
    description: 'Tax and GST compliance services for proprietorship firms. Complete bookkeeping, GST returns, and Income Tax filing support from expert CAs.',
    keywords: 'proprietorship compliance, GST filing, ITR-3, ITR-4, sole proprietor tax, business compliance',
    
    detailedDescription: (
      <div className="space-y-4">
        <p>
          While proprietorships don't require MCA compliance, they must maintain proper books of accounts and file 
          Income Tax Returns (as business income is added to personal income). If GST registered, monthly/quarterly 
          GST returns are mandatory. Our compliance package ensures your proprietorship stays tax compliant.
        </p>
        <p>
          <strong>Tax Planning for Proprietors:</strong> Business expenses can be claimed as deductions, reducing 
          taxable income. We help identify all eligible deductions – rent, utilities, travel, depreciation, and more. 
          For eligible businesses, we also advise on presumptive taxation (Section 44AD/44ADA) for simplified compliance.
        </p>
      </div>
    ),
    
    benefits: [
      'Proper books of accounts maintenance',
      'GST return filing (GSTR-1, 3B) – monthly/quarterly',
      'Income Tax Return (ITR-3 or ITR-4) filing',
      'Tax planning to minimize liability',
      'Expense categorization and deduction claims',
      'TDS compliance and return filing',
      'Tax audit support (if turnover &gt; ₹1 crore)',
      'Year-round tax consultation',
    ],
    
    documents: [
      'All sales and purchase invoices',
      'Bank statements for the year',
      'Expense bills categorized by type',
      'Rent agreement and utility bills',
      'Investment proofs (if claiming deductions)',
      'Previous year ITR acknowledgment',
      'GST portal access credentials',
      'TDS certificates received (Form 16A)',
    ],
    
    process: [
      'Monthly/quarterly books of accounts preparation',
      'GST return filing before due dates',
      'Annual accounts finalization',
      'Tax liability calculation and planning',
      'Advance tax payment reminders and challan preparation',
      'Income Tax Return filing by 31st July',
      'Tax audit (if applicable) by 30th September',
    ],
    
    timeline: 'Monthly/Quarterly/Annual',
    pricing: '₹8,999/year',
    
    additionalInfo: (
      <div className="space-y-3">
        <p><strong>GST Return Due Dates:</strong></p>
        <ul className="list-disc list-inside ml-4 space-y-1">
          <li>GSTR-1 (Sales): 11th of next month (monthly) or quarterly</li>
          <li>GSTR-3B (Summary): 20th of next month (monthly) or quarterly</li>
          <li>Annual Return GSTR-9: 31st December</li>
        </ul>
        <p className="mt-3"><strong>Presumptive Taxation (Simplified):</strong></p>
        <ul className="list-disc list-inside ml-4 space-y-1">
          <li>Section 44AD: For businesses with turnover up to ₹2 crore – 8% deemed profit</li>
          <li>Section 44ADA: For professionals with receipts up to ₹50 lakhs – 50% deemed profit</li>
          <li>No requirement to maintain detailed books of accounts</li>
        </ul>
      </div>
    ),
    
    faqs: [
      {
        question: 'Is audit required for proprietorship?',
        answer: 'Tax audit is mandatory if turnover exceeds ₹1 crore (or ₹10 crore if 95% transactions are digital). Below this threshold, audit is not required. Businesses under presumptive taxation don\'t need audit regardless of turnover.',
      },
      {
        question: 'What ITR form should proprietor file?',
        answer: 'ITR-3 for regular business income with detailed books of accounts. ITR-4 (Sugam) for businesses under presumptive taxation scheme. ITR-4 is simpler with fewer disclosures required.',
      },
      {
        question: 'How is proprietorship income taxed?',
        answer: 'Proprietorship income is added to the proprietor\'s personal income and taxed as per income tax slab rates. Business expenses are deducted before calculating taxable profit. No separate business tax like companies.',
      },
    ],
  },
  
  section8: {
    title: 'Section 8 Company Compliance',
    description: 'Compliance services for NGOs and non-profit Section 8 Companies. Expert handling of ROC, Income Tax, 80G, 12A, and FCRA compliance requirements.',
    keywords: 'Section 8 compliance, NGO compliance, 80G compliance, 12A compliance, FCRA return, non-profit compliance',
    
    detailedDescription: (
      <div className="space-y-4">
        <p>
          Section 8 Companies have compliance requirements similar to Private Limited Companies, plus additional 
          requirements to maintain tax exemption status (12A) and donor tax benefit eligibility (80G). If registered 
          under FCRA, annual foreign contribution returns are also mandatory. Our comprehensive package covers all aspects.
        </p>
        <p>
          <strong>Maintaining Tax Exempt Status:</strong> 12A and 80G registrations require ongoing compliance – proper 
          utilization of funds for charitable purposes, maintenance of records, and timely filing of returns. Non-compliance 
          can lead to cancellation of exemptions and retrospective tax demands.
        </p>
      </div>
    ),
    
    benefits: [
      'ROC compliance – AOC-4 and MGT-7 filing',
      'Board meetings and AGM documentation',
      'Financial statements preparation as per Schedule III',
      'Statutory audit by qualified Chartered Accountant',
      'Income Tax Return (ITR-7) filing',
      '12A and 80G compliance monitoring',
      'FCRA annual return (FC-4) if applicable',
      'CSR funding compliance support',
      'Activity report and beneficiary records',
      'Donor management and receipts issuance',
    ],
    
    documents: [
      'Financial statements of previous year',
      'Bank statements (all accounts including FCRA)',
      'Donation receipts and donor records',
      'Expense vouchers with beneficiary details',
      'Activity reports and project documentation',
      'Minutes of board meetings and AGM',
      'DSC of authorized signatory',
      'Previous year tax filings',
      'FCRA registration (if applicable)',
      '12A and 80G registration certificates',
    ],
    
    process: [
      'Monthly books of accounts maintenance',
      'Quarterly board meetings documentation',
      'Coordination with statutory auditor',
      'AGM convening within 6 months of FY end',
      'Filing AOC-4 within 30 days of AGM',
      'Filing MGT-7 within 60 days of AGM',
      'Income Tax Return ITR-7 by 31st October',
      'FCRA return FC-4 by 31st December (if applicable)',
    ],
    
    timeline: 'Annual',
    pricing: '₹18,999/year',
    
    additionalInfo: (
      <div className="space-y-3">
        <p><strong>Additional Compliance for Non-Profits:</strong></p>
        <ul className="list-disc list-inside ml-4 space-y-1">
          <li>12A Compliance: Apply income only for charitable purposes</li>
          <li>80G Compliance: Issue proper donation receipts with 80G number</li>
          <li>FCRA Compliance: Maintain separate bank account, file FC-4 annually</li>
          <li>CSR-1 Compliance: Annual activity report if receiving CSR funds</li>
        </ul>
        <p className="mt-3"><strong>Important Deadlines:</strong></p>
        <ul className="list-disc list-inside ml-4 space-y-1">
          <li>AGM: Within 6 months of financial year end (30th September)</li>
          <li>AOC-4: Within 30 days of AGM</li>
          <li>MGT-7: Within 60 days of AGM</li>
          <li>ITR-7: 31st October</li>
          <li>FCRA FC-4: 31st December</li>
        </ul>
      </div>
    ),
    
    faqs: [
      {
        question: 'What additional compliance is required for 80G Section 8 Company?',
        answer: '80G registered organizations must maintain proper donation records, issue receipts with 80G registration number, and ensure funds are used only for stated charitable objectives. Annual return must reflect proper utilization of donations.',
      },
      {
        question: 'What is FC-4 return?',
        answer: 'FC-4 is the annual return required for FCRA registered organizations, reporting all foreign contributions received, sources, and utilization. It must be filed on FCRA portal by 31st December every year, even if no foreign contribution was received.',
      },
      {
        question: 'Can Section 8 Company receive CSR funds?',
        answer: 'Yes, Section 8 Companies can receive CSR funds after obtaining CSR-1 registration. They must report activities to the funding company and maintain proper records of fund utilization for specified CSR activities.',
      },
    ],
  },
};

const LLPCompliance = () => <ServiceTemplate {...complianceData.llp} />;
const OPCCompliance = () => <ServiceTemplate {...complianceData.opc} />;
const ProprietorshipCompliance = () => <ServiceTemplate {...complianceData.proprietorship} />;
const Section8Compliance = () => <ServiceTemplate {...complianceData.section8} />;

export { LLPCompliance, OPCCompliance, ProprietorshipCompliance, Section8Compliance };
